import React, { useEffect, useMemo } from 'react';
import {
  ArrowRight,
  CalendarClock,
  CalendarX,
  Cake,
  CheckCircle2,
  Clock,
  Croissant,
  Dumbbell,
  ExternalLink,
  Gauge,
  LayoutDashboard,
  MessageCircle,
  Package,
  Palette,
  Scissors,
  Sparkles,
  Truck,
  UtensilsCrossed,
  XCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getWsjCalDemoBookingUrl } from '@/lib/wsjCalUrl';
import { getWsjPedidosAdminUrl, getWsjPedidosCommercialWhatsappUrl, getWsjPedidosDemoUrl } from '@/lib/wsjPedidosUrls';
import { WsjPedidosAgendaVisual, WsjPedidosHeroMockups } from '../components/wsj-pedidos/WsjPedidosHeroMockups';
import { WsjPedidosPlansSection } from '../components/wsj-pedidos/WsjPedidosPlansSection';
import { WsjPedidosSaaSFlowSection } from '../components/wsj-pedidos/WsjPedidosSaaSFlowSection';
import { WsjPedidosProblemsSection } from '../components/wsj-pedidos/WsjPedidosProblemsSection';
import { WsjPedidosUnifiedFeaturesSection } from '../components/wsj-pedidos/WsjPedidosUnifiedFeaturesSection';
import { WsjPedidosPremiumCta } from '../components/wsj-pedidos/WsjPedidosPremiumCta';
import { formatWsjPedidosPrice, getWsjPedidosAmounts } from '@/lib/wsjPedidosMoney';

const businessIcons = [Scissors, Sparkles, Palette, Sparkles, Cake, Croissant, UtensilsCrossed, Dumbbell];

const controlIcons = [
  UtensilsCrossed,
  MessageCircle,
  LayoutDashboard,
  Truck,
  Package,
  Clock,
  Gauge,
  CalendarX
];

function CtaStrip({ title, subtitle, demoUrl, adminUrl, onWhatsapp, p }) {
  return (
    <div className="rounded-2xl border border-amber-500/15 bg-gradient-to-r from-amber-500/[0.08] via-slate-950/40 to-emerald-500/[0.06] backdrop-blur-md px-5 py-6 sm:px-8 sm:py-7">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight break-words">{title}</h3>
          <p className="text-sm text-white/50 mt-1.5 font-light max-w-xl leading-relaxed break-words min-w-0">{subtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 shrink-0">
          <Button
            asChild
            className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-md shadow-amber-900/20"
          >
            <a href={demoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
              {p.btnDemo}
              <ExternalLink className="w-4 h-4" aria-hidden />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/10"
          >
            <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
              {p.btnAdmin}
              <ExternalLink className="w-4 h-4" aria-hidden />
            </a>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15"
            onClick={onWhatsapp}
          >
            <MessageCircle className="w-4 h-4 mr-2" aria-hidden />
            {p.btnWhatsapp}
          </Button>
        </div>
      </div>
    </div>
  );
}

const WsjPedidos = () => {
  const { t, language } = useLanguage();
  const p = t.wsjPedidosPage;
  const demoUrl = getWsjCalDemoBookingUrl();
  const menuDemoUrl = getWsjPedidosDemoUrl();
  const adminUrl = getWsjPedidosAdminUrl();

  const amounts = getWsjPedidosAmounts();
  const heroMock = useMemo(() => {
    const fmt = (value) => formatWsjPedidosPrice(value, language);
    return {
      ...p.heroMock,
      orderTotal: fmt(amounts.heroOrder),
      productPrice: `${p.heroMock.priceFromPrefix}${fmt(amounts.heroProductFrom)}`
    };
  }, [language, p.heroMock, amounts.heroOrder, amounts.heroProductFrom]);

  useEffect(() => {
    const prev = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prev;
    };
  }, [p.metaTitle]);

  const openWhatsapp = () => {
    window.open(getWsjPedidosCommercialWhatsappUrl(p.whatsappMessage), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden">
      <Header />
      <main className="relative overflow-x-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0 tech-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute top-24 left-1/4 w-[min(420px,calc(100vw-2rem))] max-w-[100vw] h-[min(420px,70vw)] bg-amber-500/[0.07] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-40 right-0 w-[min(380px,calc(100vw-2rem))] max-w-[100vw] h-[min(380px,65vw)] bg-emerald-500/[0.06] rounded-full blur-[90px] pointer-events-none" />

        <div className="relative z-10 container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section className="min-w-0 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 xl:gap-16 items-center mb-16 sm:mb-24 lg:mb-28">
            <div className="text-center lg:text-left pt-2 min-w-0">
              <div className="inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold tracking-wide text-amber-200/90 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden />
                <span className="break-words text-center">{p.badge}</span>
              </div>
              <h1 className="text-[1.65rem] sm:text-4xl lg:text-[2.65rem] xl:text-5xl font-black text-white leading-[1.12] tracking-tight mb-5 break-words">
                {p.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 mb-3 break-words">
                {p.heroSubtitle}
              </p>
              <p className="text-xs sm:text-sm text-white/35 font-medium tracking-wide mb-8 break-words">{p.heroTrust}</p>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3 mb-8">
                <Button
                  asChild
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-6"
                >
                  <a href={menuDemoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnDemo}
                    <ExternalLink className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/[0.1] h-12 px-6"
                >
                  <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnAdmin}
                    <ExternalLink className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15 h-12 px-6"
                  onClick={openWhatsapp}
                >
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 relative min-w-0">
              <WsjPedidosHeroMockups mock={heroMock} />
            </div>
          </section>

          <div className="max-w-5xl mx-auto mb-16 sm:mb-20">
            <CtaStrip
              title={p.midCtaTitle}
              subtitle={p.midCtaSubtitle}
              demoUrl={menuDemoUrl}
              adminUrl={adminUrl}
              onWhatsapp={openWhatsapp}
              p={p}
            />
          </div>

          <WsjPedidosProblemsSection copy={p} />
          <WsjPedidosPlansSection
            language={language}
            copy={p}
            demoUrl={menuDemoUrl}
            adminUrl={adminUrl}
            onWhatsapp={openWhatsapp}
          />
          <WsjPedidosSaaSFlowSection copy={p} />

          {/* PARA QUEM */}
          <section id="wsj-pedidos-business" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-4">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">{p.sectionBusiness}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionBusinessLead}</p>
            </div>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto rounded-full mb-12 sm:mb-14" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {p.businessTypes.map((b, i) => {
                const Icon = businessIcons[i] || Sparkles;
                return (
                  <div
                    key={b.title}
                    className="group overflow-x-hidden rounded-2xl border border-white/[0.08] bg-slate-950/35 backdrop-blur-md p-5 sm:p-6 hover:border-amber-500/25 hover:bg-slate-950/55 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500/25 to-transparent border border-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <Icon className="w-5 h-5 text-amber-400" aria-hidden />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 break-words">{b.title}</h3>
                    <p className="text-sm text-white/45 leading-relaxed font-light break-words">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CONTROLE TOTAL */}
          <section id="wsj-pedidos-control" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionControl}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionControlLead}</p>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {p.controlFeatures.map((f, i) => {
                const Icon = controlIcons[i] || Sparkles;
                return (
                  <div
                    key={f.title}
                    className="overflow-x-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.05] to-transparent p-5 sm:p-6"
                  >
                    <Icon className="w-6 h-6 text-amber-400/90 mb-4" aria-hidden />
                    <h3 className="text-base font-bold text-white mb-2 break-words">{f.title}</h3>
                    <p className="text-sm text-white/45 leading-relaxed font-light break-words">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <WsjPedidosUnifiedFeaturesSection copy={p} />

          {/* ANTES E DEPOIS */}
          <section id="wsj-pedidos-compare" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionCompare}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionCompareLead}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <div className="rounded-3xl border border-red-500/15 bg-red-950/[0.12] backdrop-blur-md p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-6 min-w-0">
                  <XCircle className="w-6 h-6 text-red-400/90 shrink-0" aria-hidden />
                  <h3 className="text-xl font-bold text-red-100/95 break-words min-w-0">{p.beforeTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {p.beforeItems.map((line) => (
                    <li key={line} className="flex gap-3 text-red-100/70 text-sm leading-relaxed min-w-0">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400/50" aria-hidden />
                      <span className="break-words min-w-0">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-emerald-500/20 bg-emerald-950/[0.14] backdrop-blur-md p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-6 min-w-0">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" aria-hidden />
                  <h3 className="text-xl font-bold text-emerald-100/95 break-words min-w-0">{p.afterTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {p.afterItems.map((line) => (
                    <li key={line} className="flex gap-3 text-emerald-100/75 text-sm leading-relaxed min-w-0">
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500/80 mt-0.5" aria-hidden />
                      <span className="break-words min-w-0">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="max-w-5xl mx-auto mb-20 sm:mb-24">
            <CtaStrip
              title={p.btnWant}
              subtitle={p.sectionCtaLead}
              demoUrl={menuDemoUrl}
              adminUrl={adminUrl}
              onWhatsapp={openWhatsapp}
              p={p}
            />
          </div>

          {/* AGENDA */}
          <section id="wsj-pedidos-agenda" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 items-center">
              <div>
                <h2 className="text-2xl sm:text-4xl font-black text-white mb-4">{p.sectionAgenda}</h2>
                  <p className="text-white/50 font-light leading-relaxed mb-8 break-words min-w-0">{p.sectionAgendaLead}</p>
                <ul className="space-y-5">
                  {p.agendaPoints.map((pt) => (
                    <li key={pt.title} className="flex gap-4">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-500/25 bg-amber-500/10">
                        <CalendarClock className="w-4 h-4 text-amber-400" aria-hidden />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{pt.title}</p>
                        <p className="text-sm text-white/45 mt-1 font-light leading-relaxed">{pt.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-10 lg:mt-0">
                <WsjPedidosAgendaVisual />
              </div>
            </div>
          </section>

          {/* COMO FUNCIONA */}
          <section id="wsj-pedidos-how" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionHow}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionHowLead}</p>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-amber-500/25 to-transparent" aria-hidden />
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                {p.howSteps.map((step, i) => (
                  <div
                    key={step.title}
                    className="relative min-w-0 overflow-x-hidden rounded-2xl border border-white/[0.08] bg-slate-950/50 backdrop-blur-md p-5 pt-8 text-center lg:text-left"
                  >
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 lg:left-5 lg:translate-x-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-slate-950 font-black text-sm flex items-center justify-center shadow-lg">
                      {i + 1}
                    </div>
                    <h3 className="text-sm font-bold text-white mt-4 mb-2 leading-snug break-words">{step.title}</h3>
                    <p className="text-xs text-white/45 leading-relaxed font-light break-words">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* DEMONSTRAÇÃO */}
          <section id="wsj-pedidos-demo" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionDemo}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionDemoLead}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div className="flex min-w-0 flex-col rounded-3xl border border-white/[0.1] bg-slate-950/40 backdrop-blur-md p-6 sm:p-8 min-h-[280px]">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center mb-5">
                  <UtensilsCrossed className="w-6 h-6 text-amber-400" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 break-words">{p.demoCardMenuTitle}</h3>
                <p className="text-sm text-white/45 font-light leading-relaxed flex-1 break-words">{p.demoCardMenuDesc}</p>
                <p className="text-xs font-semibold text-amber-400/90 mt-3 tabular-nums tracking-tight">
                  {p.demoCardMenuPricePrefix}
                  {formatWsjPedidosPrice(amounts.demoMenuFrom, language)}
                </p>
                <Button asChild className="mt-6 rounded-xl w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold">
                  <a href={menuDemoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnDemoMenu}
                    <ExternalLink className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
              </div>
              <div className="flex min-w-0 flex-col rounded-3xl border border-white/[0.1] bg-slate-950/40 backdrop-blur-md p-6 sm:p-8 min-h-[280px]">
                <div className="w-12 h-12 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center mb-5">
                  <LayoutDashboard className="w-6 h-6 text-white/80" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 break-words">{p.demoCardAdminTitle}</h3>
                <p className="text-sm text-white/45 font-light leading-relaxed flex-1 break-words">{p.demoCardAdminDesc}</p>
                <Button asChild variant="outline" className="mt-6 rounded-xl w-full border-white/20 text-white hover:bg-white/10">
                  <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnDemoAdmin}
                    <ExternalLink className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
              </div>
              <div className="flex min-w-0 flex-col rounded-3xl border border-emerald-500/20 bg-emerald-950/[0.08] backdrop-blur-md p-6 sm:p-8 min-h-[280px]">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
                  <MessageCircle className="w-6 h-6 text-emerald-400" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 break-words">{p.demoCardWsjTitle}</h3>
                <p className="text-sm text-white/45 font-light leading-relaxed flex-1 break-words">{p.demoCardWsjDesc}</p>
                <Button
                  type="button"
                  className="mt-6 rounded-xl w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                  onClick={openWhatsapp}
                >
                  {p.btnTalkWsj}
                  <ArrowRight className="w-4 h-4 ml-2 inline" aria-hidden />
                </Button>
              </div>
            </div>
          </section>

          <WsjPedidosPremiumCta copy={p} demoUrl={demoUrl} onWhatsapp={openWhatsapp} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WsjPedidos;
